import React from 'react';
import PropTypes from 'prop-types';
import VisitsTable from './VisitsTable';
import * as useFetchVisitsModule from './useFetchVisits'; // Import of the module to override it
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Mock Data
const generateMockVisits = (count) => {
  const visits = [];
  for (let i = 1; i <= count; i += 1) {
    visits.push({
      id: i,
      date: `2024-11-${String(i).padStart(2, '0')}`,
      site_name: `Site ${i}`,
      completed: i % 2 === 0,
      purpose: i % 3 === 0 ? 'Routine Visit' : 'Follow-Up Visit',
      leader: `Leader ${i}`,
    });
  }
  return visits;
};

const mockVisits = generateMockVisits(20);

// Mock useFetchVisits
// eslint-disable-next-line no-import-assign, import/namespace
useFetchVisitsModule.default = () => {
  return mockVisits;
};

export default {
  title: 'Components/Visits/VisitsTable',
  component: VisitsTable,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `VisitsTable` component displays visit records in a structured table format. It allows users to review and sort visit details efficiently.\n\n',
      // When & how to use it?
      '\nUse the `VisitsTable` component when a tabular representation of visit records is needed. It supports sortable columns and integrates with backend APIs for dynamic data fetching.\n\n',
      // Current Usage
      '\nThe `VisitsTable` component is currently used on the Visits page to provide a detailed view of visit records in a table format for table view. It enables users to interact with data, sort it, and review key details like visit date, status, site, purpose, and leader.\n\n',
      // Related Components
      `\n- **[Table](http://localhost:6006/?path=/docs/components-table--docs)**: The base table component used within VisitsTable.
       \n- **useFetchVisits** (app/javascript/src/componentsV2/Visits/useFetchVisits.js): Custom hook for fetching visit data.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'Organization ID used to fetch visit records for the table.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    params: {
      control: { type: 'object' },
      description: 'Search and filter parameters for fetching data dynamically.\n\n',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    currentPage: {
      control: { type: 'number' },
      description: 'Current page number for paginated data.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    itemsPerPage: {
      control: { type: 'number' },
      description: 'Number of items displayed per page.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    setTotalVisits: {
      control: { type: 'function' },
      description: 'Callback function to set the total number of visits for pagination.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    onSort: {
      control: { type: 'function' },
      description: 'Callback function triggered when sorting columns.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    isLoading: {
      control: { type: 'boolean' },
      description: 'Determines whether the table is in a loading state.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
        defaultValue: { summary: 'false' },
      },
    },
  },
};

const VisitsTableTemplate = ({ visits, onSort, isLoading = false }) => (

  <VisitsTable visits={visits} onSort={onSort} isLoading={isLoading} />
);

VisitsTableTemplate.propTypes = {
  visits: PropTypes.array.isRequired,
  onSort: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

const Template = VisitsTableTemplate.bind({});

export const DefaultVisitsTable = Template.bind({});
DefaultVisitsTable.args = {
  visits: mockVisits,
  onSort: () => { },
  isLoading: false,
};
DefaultVisitsTable.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when the user toggles table view button on the `VisitsPage` to display visit records in a structured table format. It shows sortable columns for details like date, site, purpose, and status. In Storybook, dummy data is displayed for component review purposes because API calls are not possible.\n\n',
  // Where is it currently used on the product?
  '\nThe `VisitsTable` is currently used on the Visits page for displaying visit records in a sortable table format.\n\n',
);
